import { useMemo, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getClientsHeatmap } from '../../../shared/api/clientsAnalytics.api'
import type { DashboardPeriodKey } from './types'
import type { ClientsAnalyticsState } from './clientsAnalyticsState'

interface Props {
  periodKey: DashboardPeriodKey
  customFrom: string | null
  customTo: string | null
  state: ClientsAnalyticsState
  /** Контейнер, к которому скроллить при клике по ячейке (donut выше). */
  scrollTargetRef?: React.RefObject<HTMLDivElement>
}

function buildQuery(
  periodKey: DashboardPeriodKey,
  customFrom: string | null,
  customTo: string | null,
): {
  period: 'day' | 'week' | 'month' | 'quarter' | 'year' | 'custom'
  from?: string
  to?: string
} | null {
  if (periodKey === 'custom') {
    if (!customFrom || !customTo) return null
    return { period: 'custom', from: customFrom, to: customTo }
  }
  return { period: periodKey }
}

// Интенсивность зелёного по доле от максимума ячейки.
function cellStyle(value: number, max: number): React.CSSProperties {
  if (value === 0) return { background: 'transparent', color: '#9ca3af' }
  const intensity = max > 0 ? value / max : 0
  const alpha = 0.15 + intensity * 0.65
  return {
    background: `rgba(16, 185, 129, ${alpha})`,
    color: intensity > 0.55 ? '#fff' : '#065f46',
  }
}

export function ClientsHeatmapCard({
  periodKey,
  customFrom,
  customTo,
  state,
  scrollTargetRef,
}: Props) {
  const periodQuery = useMemo(
    () => buildQuery(periodKey, customFrom, customTo),
    [periodKey, customFrom, customTo],
  )
  const internalRef = useRef<HTMLDivElement>(null)

  const enabled = Boolean(periodQuery)
  const { data, isLoading } = useQuery({
    queryKey: ['clients-heatmap', state.filters, periodQuery],
    queryFn: () =>
      getClientsHeatmap({
        ...periodQuery!,
        managerIds: [],
        filters: state.filters,
      }),
    enabled,
  })

  if (!periodQuery) return null

  return (
    <div
      ref={internalRef}
      className="bg-white rounded-2xl border border-gray-200 p-4 flex flex-col gap-3"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-sm text-gray-500">Сегмент × город</div>
          <div className="text-xs text-gray-400">
            Вся клиентская база. Города с 1 клиентом — в колонке «Прочее». Клик по ячейке — добавить
            два фильтра.
          </div>
        </div>
      </div>

      {isLoading || !data ? (
        <div className="text-sm text-gray-400 px-2 py-8 text-center">Загрузка…</div>
      ) : data.total === 0 ? (
        <div className="text-sm text-gray-500 px-2 py-8 text-center">
          Нет клиентов под выборку и фильтры.
        </div>
      ) : (
        <HeatmapTable
          data={data}
          onCellClick={(seg, city) => {
            state.addFilter('segment', seg)
            state.addFilter('city', city)
            scrollTargetRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }}
        />
      )}
    </div>
  )
}

function HeatmapTable({
  data,
  onCellClick,
}: {
  data: {
    segments: string[]
    cities: string[]
    matrix: Record<string, Record<string, number>>
    rowTotals: Record<string, number>
    colTotals: Record<string, number>
    total: number
  }
  onCellClick: (segment: string, city: string) => void
}) {
  // Максимум для интенсивности — по ячейкам (не итогам).
  let max = 0
  for (const seg of data.segments) {
    for (const city of data.cities) {
      const v = data.matrix[seg]?.[city] ?? 0
      if (v > max) max = v
    }
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs border-separate border-spacing-0">
        <thead>
          <tr>
            <th className="text-left px-2 py-1.5 text-gray-500 font-medium">Сегмент / Город</th>
            {data.cities.map((c) => (
              <th key={c} className="text-center px-2 py-1.5 text-gray-500 font-medium">
                {c}
              </th>
            ))}
            <th className="text-center px-2 py-1.5 text-gray-700 font-semibold bg-gray-50 rounded-tr-md">
              Всего
            </th>
          </tr>
        </thead>
        <tbody>
          {data.segments.map((seg) => (
            <tr key={seg}>
              <td className="px-2 py-1.5 text-gray-700 font-medium border-t border-gray-100">
                {seg}
              </td>
              {data.cities.map((city) => {
                const v = data.matrix[seg]?.[city] ?? 0
                // Колонка «Прочее» — свёрнутые мелкие города, точного фильтра нет.
                const cellClickable = v > 0 && city !== 'Прочее'
                return (
                  <td
                    key={`${seg}|${city}`}
                    onClick={() => cellClickable && onCellClick(seg, city)}
                    className={`text-center px-2 py-1.5 border-t border-gray-100 tabular-nums ${
                      cellClickable ? 'cursor-pointer hover:brightness-95' : ''
                    }`}
                    style={cellStyle(v, max)}
                    title={
                      v > 0 ? `${seg} · ${city}: ${v} клиентов` : `${seg} · ${city}: 0 клиентов`
                    }
                  >
                    {v || ''}
                  </td>
                )
              })}
              <td className="text-center px-2 py-1.5 border-t border-gray-100 bg-gray-50 text-gray-700 font-semibold tabular-nums">
                {data.rowTotals[seg] ?? 0}
              </td>
            </tr>
          ))}
          <tr>
            <td className="px-2 py-1.5 text-gray-700 font-semibold bg-gray-50 border-t border-gray-100 rounded-bl-md">
              Всего
            </td>
            {data.cities.map((city) => (
              <td
                key={`total|${city}`}
                className="text-center px-2 py-1.5 bg-gray-50 border-t border-gray-100 text-gray-700 font-semibold tabular-nums"
              >
                {data.colTotals[city] ?? 0}
              </td>
            ))}
            <td className="text-center px-2 py-1.5 bg-gray-100 border-t border-gray-100 text-gray-900 font-bold tabular-nums rounded-br-md">
              {data.total}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}
